/**
 * A yes/no choice the person made about the screen — the rail collapsed
 * (D-084), the job's notes panel open (D-083) — as the browser remembers it
 * between the first paint and the API's answer. The person's preference
 * (through `/api/preferences`) is the truth across machines; a local copy keeps
 * the screen from jumping on load. Pure, so the reconciliation is tested.
 */

export const NAV_COLLAPSED_STORAGE_KEY = 'unitedos.nav.collapsed';
export const NOTES_OPEN_STORAGE_KEY = 'unitedos.job.notes_open';
export const NOTES_WIDTH_STORAGE_KEY = 'unitedos.job.notes_width';
export const SCHEDULE_VIEW_STORAGE_KEY = 'unitedos.schedule.view';

/** The notes panel's width (D-085): the bounds the API also holds. */
export const NOTES_WIDTH_MIN = 260;
export const NOTES_WIDTH_MAX = 640;
export const NOTES_WIDTH_DEFAULT = 320;

export function clampInt(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

/** A stored whole number within bounds, or null when absent, junk or out of range. */
export function readStoredInt(store: KeyValueStore | null, key: string, min: number, max: number): number | null {
  try {
    const raw = store?.getItem(key);
    if (raw === null || raw === undefined || !/^-?\d+$/.test(raw)) return null;
    const n = Number(raw);
    return n >= min && n <= max ? n : null;
  } catch {
    return null;
  }
}

export function storeInt(store: KeyValueStore | null, key: string, value: number): void {
  try {
    store?.setItem(key, String(Math.round(value)));
  } catch {
    // Storage refused: the preference on the API still holds it.
  }
}

/** The width once the API has answered: the saved one when valid, else the local copy, else the default. */
export function resolveInt(stored: number | null, remote: number | null | undefined, fallback: number, min: number, max: number): number {
  if (typeof remote === 'number' && Number.isInteger(remote) && remote >= min && remote <= max) return remote;
  return stored ?? fallback;
}

/** The minimum of Storage this needs, so a test can hand in a plain object. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The local copy, or null when there is none or storage is unavailable (a private window, a blocked origin). */
export function readStoredFlag(store: KeyValueStore | null, key: string): boolean | null {
  try {
    const raw = store?.getItem(key);
    return raw === '1' ? true : raw === '0' ? false : null;
  } catch {
    return null;
  }
}

export function storeFlag(store: KeyValueStore | null, key: string, value: boolean): void {
  try {
    store?.setItem(key, value ? '1' : '0');
  } catch {
    // Storage refused (quota, privacy mode): the preference on the API still holds it.
  }
}

/**
 * What the screen should be once the API has answered: the person's saved preference when they
 * have one, else whatever the browser remembered, else the default.
 */
export function resolveFlag(stored: boolean | null, remote: boolean | null | undefined, fallback: boolean): boolean {
  if (remote === true || remote === false) return remote;
  return stored ?? fallback;
}

/** `window.localStorage`, or null where touching it throws. */
export function browserStorage(): KeyValueStore | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** A stored choice from a fixed list (the schedule's view), or null when absent, junk or not on the list. */
export function readStoredChoice<T extends string>(store: KeyValueStore | null, key: string, allowed: readonly T[]): T | null {
  try {
    const raw = store?.getItem(key);
    return raw !== null && raw !== undefined && (allowed as readonly string[]).includes(raw) ? (raw as T) : null;
  } catch {
    return null;
  }
}

export function storeChoice(store: KeyValueStore | null, key: string, value: string): void {
  try {
    store?.setItem(key, value);
  } catch {
    // Storage refused: the preference on the API still holds it.
  }
}

/**
 * The schedule's view when the address names none: the browser's copy first — it is this person's
 * latest choice here, written at every change, and a view that switched after the first paint
 * would re-read the schedule under them — then the saved preference (a new browser), then the
 * default. The opposite order from `resolveFlag`, on purpose.
 */
export function resolveChoice<T extends string>(stored: T | null, remote: string | null | undefined, allowed: readonly T[], fallback: T): T {
  if (stored !== null) return stored;
  if (typeof remote === 'string' && (allowed as readonly string[]).includes(remote)) return remote as T;
  return fallback;
}
